/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { getStylist, NestedCSSProperties } from 'src/core/themes/luisStylist';

type ContainerSize = 'main' | 'side' | 'none';

export type ContainerProps = IStackProps & {
    /**
     * The projected elements passed from the consumer between
     * the dialog tags.
     */
    children?: React.ReactNode;

    /**
     * The width size around the container.
     */
    size?: ContainerSize;

    /**
     * Flag if the container is scrollable.
     */
    overflow?: boolean;
};

const { styleComponent } = getStylist('Container');

const StyledContainer = styleComponent(Stack)<{ size: ContainerSize; overflow: boolean }>(
    'StyledContainer',
    { backgroundColor: 'transparent' },
    ({ customProps: { size, overflow } }, theme) => {
        let style: NestedCSSProperties = { height: 'fit-content' };

        switch (size) {
            case 'main':
                style = {
                    ...style,
                    padding: theme.mainPadding,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    width: theme.mainWidth,
                    maxWidth: theme.mainMaxWidth,
                    height: 'fit-content'
                };
                break;
            case 'side':
                style = { ...style, padding: theme.side, width: '100%' };
                break;
            default:
        }

        if (overflow) {
            style = { ...style, overflowY: 'auto' };
        }

        return style;
    }
);

/**
 * A unified container component that wraps children into a flex
 * display, and adds padding based on container size.
 *
 * @param props The input to the customize the container.
 */
export const Container = (props: ContainerProps) => {
    const { children, size = 'side', overflow = true, ...rest } = props;

    return (
        <StyledContainer customProps={{ size, overflow }} {...rest}>
            {children}
        </StyledContainer>
    );
};
